import React, { useCallback, useMemo } from 'react'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { groupBy, sortBy } from 'lib/array/arrayUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { OfferPageActions, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { capitalise } from 'lib/string/stringUtils'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import IndividualRoomSelectRoom from './IndividualRoomSelectRoom'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import useSuggestedPackageOption from 'hooks/OfferPage/useSuggestedPackageOption'

interface Props {
  offer: App.HotelOffer
  availablePackages: Array<App.OfferPackageOptionView>
  rooms: Array<OfferPageRoom>
  roomId: string
  checkInDate: string | undefined
  checkOutDate: string | undefined
}

function IndividualRoomPackageSelectionModal(props: Props) {
  const {
    offer,
    availablePackages,
    rooms,
    roomId,
  } = props

  const modalElement = useModalElementContext()

  const roomIndex = rooms.findIndex(r => r.id === roomId)

  const room = rooms[roomIndex]

  const dispatch = useOfferPageDispatch()

  const close = useCallback(() => { modalElement.resolve() }, [modalElement])

  const suggestedPackageOption = useSuggestedPackageOption({ room })

  const onOptionSelect = useCallback((packageOption: App.HotelPackage) => {
    if (packageOption) {
      dispatch({
        type: OfferPageActions.selectRoomPackage,
        packageOption,
        room,
      })
    }
    showSnackbar(`${capitalise(offer.saleUnit)} ${roomIndex + 1} has been updated`, 'success', { action: { label: 'OK' } })
    close()
  }, [dispatch, offer.saleUnit, room, roomIndex, close])

  const optionsByRoomType = useMemo(() => (
    sortBy(
      Array.from(groupBy(availablePackages, (pkg) => pkg.package.roomType.id).values()),
      packageViews => packageViews[0].package.id === room.roomId ? 0 : 1,
      'asc',
    )
  ), [availablePackages, room.roomId])

  const isDesktopLayout = useScreenSizeUp('tablet')

  const isHorizontalSliderEnabled = !isDesktopLayout

  return (
    <ModalBase height="full" size="L">
      <ModalHeader
        title={`${capitalise(offer.saleUnit)} ${roomIndex + 1} of ${rooms.length}`}
        subtitle={formatOccupantsShort([room])}
        onCloseButtonClick={close}
      />
      <ModalBody>
        <Pane background="neutral-six" tabletBackground="none" padding="8 0 0 0" tabletPadding={12} desktopPadding={20}>
          <Group direction="vertical" gap={8} tabletGap={20}>
            {optionsByRoomType.map((packageOptions) => (
              <IndividualRoomSelectRoom
                key={packageOptions[0].package.roomType.id}
                currentRoom={room}
                onSelect={onOptionSelect}
                optionVariant={isHorizontalSliderEnabled ? 'carousel' : 'stacked'}
                packageViewOptions={packageOptions}
                suggestedPackageOption={suggestedPackageOption}
              />
            ))}
          </Group>
        </Pane>
      </ModalBody>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  rooms: state.rooms,
}), IndividualRoomPackageSelectionModal)
